import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';

import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import CardActions from '@mui/material/CardActions';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import IconButton from '@mui/material/IconButton';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Grid from '@mui/material/Grid';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Button from '@mui/material/Button';
import MuiLink from '@mui/material/Link';
import { css } from '@emotion/react';

import { buildPath, invariant } from '@e-socna/utils';
import { getFragmentData } from '@e-socna/data-layer';
import { EMPTY_OBJECT } from '@e-socna/empty';
import type {
  Id,
  FragmentType,
  CivilityId,
  ThirdPartyCategoryId,
  ActorCategoryId,
  UserRoleId,
} from '@e-socna/data-layer';

import { useFindOnePersonQuery } from '../../../pages/Person/hooks/useFindOnePersonQuery.hook';
import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { PATH_PERSON_DEFAULT } from '../../../app/constants/paths.constant';

import { LoadingCard } from '../../LoadingCard';
import { MenuItem } from '../../Menu/MenuItem';
import { useActionMenu } from '../../Menu/hooks/useActionMenu.hook';
import { Avatar } from '../../Avatar';
import { thirdPartyTooltipOf } from '../../../utils/third-party-tooltip.util';
import { useActorCategoriesLabel } from '../../FolderBusinessEntityList/hooks/useActorCategoriesLabel.hook';
import { ExpandMoreCard } from '../../ExpandMoreCard';
import type { Option } from '../../Menu/MenuItem/types/option.type';

import { ExternalReferencesSection } from '../../ExternalReferences/Section';

import { ACTOR_CARD_SIZE, ACTOR_CARD_SX } from '../constants/card.constant';

import { ActorCardCmp_Person_Fragment } from './fragments/person.fragment';
import { ActorCardCmp_ActorPerson_Fragment } from './fragments/actor-person.fragment';
import { ContactInfo } from './ContactInfo';
import { FastIcons } from './FastIcons';

interface Props {
  readonly actionsEnabled?: boolean;
  readonly actor: FragmentType<typeof ActorCardCmp_ActorPerson_Fragment>;
  readonly consultThirdPartyInNewTab: boolean;
  readonly onStartRemove?: (id: Id) => void;
  readonly onStartUpdate?: (id: Id) => void;
}

export const PersonActorCard = ({
  actionsEnabled = false,
  actor: pActor,
  consultThirdPartyInNewTab,
  onStartRemove: handleStartRemove,
  onStartUpdate: handleStartUpdate,
}: Readonly<Props>) => {
  invariant(
    !actionsEnabled || !!(handleStartRemove && handleStartUpdate),
    'onStartRemove & onStartUpdate are required when actionsEnabled is true',
  );

  const {
    civilitiesIdMap,
    thirdPartyCategoriesIdMap,
    actorCategoriesIdMap,
    userRolesIdMap,
  } = useReferencesContext();

  const actor = getFragmentData(ActorCardCmp_ActorPerson_Fragment, pActor);

  const {
    anchorEl,
    handleCloseActionMenu,
    handleOpenActionMenu,
    isActionMenuOpen,
    options,
  } = useActionMenu({
    id: actor._id,
    onStartRemove: handleStartRemove,
    onStartUpdate: handleStartUpdate,
  });

  const getCategoriesLabel = useActorCategoriesLabel();

  const [expanded, setExpanded] = useState(false);
  const handleExpandClick = useCallback(() => {
    setExpanded((prevExpanded) => !prevExpanded);
  }, []);

  const { data: rowPersonData, isSuccess: personIsSuccess } =
    useFindOnePersonQuery(actor.thirdPartyId);

  if (!personIsSuccess) {
    return (
      <Grid size={ACTOR_CARD_SIZE} sx={ACTOR_CARD_SX}>
        <LoadingCard />
      </Grid>
    );
  }

  const person = getFragmentData(
    ActorCardCmp_Person_Fragment,
    rowPersonData.findOnePerson,
  );

  const path = buildPath(PATH_PERSON_DEFAULT, { id: person._id });

  return (
    <Grid size={ACTOR_CARD_SIZE} sx={ACTOR_CARD_SX}>
      <Card sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <CardHeader
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...(actionsEnabled
            ? {
                action: (
                  <>
                    <Tooltip title="Actions sur l'Acteur">
                      <IconButton
                        aria-controls={
                          isActionMenuOpen
                            ? `${actor._id}-actions-menu`
                            : undefined
                        }
                        aria-expanded={isActionMenuOpen ? 'true' : undefined}
                        aria-haspopup="true"
                        aria-label="Actions sur l'acteur"
                        id={`${actor._id}-actions-menu-button`}
                        onClick={handleOpenActionMenu}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </Tooltip>
                    <Menu
                      anchorEl={anchorEl}
                      id={`${actor._id}-actions-menu`}
                      onClose={handleCloseActionMenu}
                      open={isActionMenuOpen}
                      slotProps={{
                        list: {
                          'aria-labelledby': `${actor._id}-actions-menu-button`,
                        },
                      }}
                    >
                      {options.map((option: Option) => (
                        <MenuItem
                          key={option.key}
                          onCloseMenu={handleCloseActionMenu}
                          option={option}
                        />
                      ))}
                    </Menu>
                  </>
                ),
              }
            : EMPTY_OBJECT)}
          avatar={
            <>
              <Avatar
                bgColor={person.color}
                fullName={person.fullName}
                imageUri={person.pictureImageUri}
                label={thirdPartyTooltipOf(
                  person.fullName,
                  getCategoriesLabel(actor.categoryIds),
                )}
              />
              <FastIcons
                categoryIds={actor.categoryIds}
                isInternal={actor.isInternal}
                isVip={person.isVip}
                useItsEdmDomain={false}
              />
            </>
          }
          css={css`
            .MuiCardHeader-action {
              display: contents;
            }
          `}
        />
        <CardContent sx={{ flexGrow: 1 }}>
          <Typography
            component="div"
            gutterBottom
            sx={{ lineHeight: 1.2 }}
            variant="h6"
          >
            {person.fullName}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 1.5 }}>
            {civilitiesIdMap[person.civilityId as CivilityId]?.label ??
              'Inconnu'}
          </Typography>
          <Typography color="text.secondary" variant="overline">
            Catégories du Tiers
          </Typography>
          <Typography gutterBottom variant="body2">
            {!person.categoryIds?.length
              ? 'Non renseignées'
              : person.categoryIds
                  .map(
                    (id) =>
                      thirdPartyCategoriesIdMap[id as ThirdPartyCategoryId]
                        ?.label ?? 'Inconnue',
                  )
                  .join(', ')}
          </Typography>
          <Typography color="text.secondary" variant="overline">
            Catégories de l&apos;Acteur
          </Typography>
          <Typography gutterBottom variant="body2">
            {!actor.categoryIds.length
              ? 'Non renseignées'
              : actor.categoryIds
                  .map(
                    (id) =>
                      actorCategoriesIdMap[id as ActorCategoryId]?.label ??
                      'Inconnue',
                  )
                  .join(', ')}
          </Typography>
          <Typography color="text.secondary" variant="overline">
            Rôles Contextuels e-SOCNA
          </Typography>
          <Typography gutterBottom variant="body2">
            {!actor.contextualRoleIds?.length
              ? 'Non renseignés'
              : actor.contextualRoleIds
                  .map(
                    (id) =>
                      userRolesIdMap[id as UserRoleId]?.label ?? 'Inconnu',
                  )
                  .join(', ')}
          </Typography>
          <ExternalReferencesSection references={actor.references} />
        </CardContent>
        <CardActions disableSpacing>
          {consultThirdPartyInNewTab ? (
            <Button
              component={MuiLink}
              endIcon={<OpenInNewIcon />}
              href={path}
              rel="noopener noreferrer"
              size="medium"
              target="_blank"
              variant="outlined"
            >
              Consulter
            </Button>
          ) : (
            <Button component={Link} size="medium" to={path} variant="outlined">
              Consulter
            </Button>
          )}
          <ExpandMoreCard
            expanded={expanded}
            onExpandClick={handleExpandClick}
          />
        </CardActions>
        <ContactInfo contactInfo={person.contactInfo} expanded={expanded} />
      </Card>
    </Grid>
  );
};
